import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import MobileNav from "@/components/MobileNav";
import api from "@/lib/api";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, RefreshCw, Trash2, ImageIcon, Bell, BellOff } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { formatOdds, formatCurrency } from "@/lib/format";

export default function SlipDetailPage() {
  const { slip_id } = useParams();
  const [slip, setSlip] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const nav = useNavigate();
  const { settings } = useSettings();

  const load = async () => {
    try { const { data } = await api.get(`/slips/${slip_id}`); setSlip(data); }
    catch { toast.error("Slip not found"); nav("/dashboard"); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post(`/slips/${slip_id}/refresh`); setSlip(data); toast.success("Updated"); }
    catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this slip?")) return;
    await api.delete(`/slips/${slip_id}`); toast.success("Deleted"); nav("/dashboard");
  };

  const toggleMute = async () => {
    const nextMuted = !slip.notifications_muted;
    try {
      await api.post(`/slips/${slip_id}/mute`, { muted: nextMuted });
      setSlip({ ...slip, notifications_muted: nextMuted });
      toast.success(nextMuted ? "Notifications muted for this slip" : "Notifications on for this slip");
    } catch { toast.error("Failed"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slip_id]);
  useEffect(() => {
    const t = setInterval(() => { if (slip && ["pending", "winning", "losing"].includes(slip.status)) refresh(); }, 30000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, [slip?.status]);

  if (!slip) return <div className="min-h-screen bg-[#0A0D14] text-slate-400 flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#0A0D14] pb-24 md:pb-8">
      <AppHeader />
      <MobileNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <button data-testid="back-button" onClick={() => nav(-1)} className="text-slate-400 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 mb-4">
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <div className="rounded-xl bg-[#121824] border border-white/10 p-5 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{slip.bookmaker || "Custom"}</div>
              <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                {slip.slip_type} · {slip.legs.length} Leg{slip.legs.length > 1 ? "s" : ""}
              </h1>
            </div>
            <StatusBadge status={slip.status} size="lg" testid="detail-status-badge" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Odds" value={formatOdds(slip.total_odds, settings.odds_format)} />
            <Stat label="Stake" value={formatCurrency(slip.stake, settings.currency)} />
            <Stat label="Returns" value={formatCurrency(slip.projected_payout, settings.currency)} accent="emerald" />
          </div>
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <Button data-testid="detail-mute" variant="outline" onClick={toggleMute} className={`border-white/10 ${slip.notifications_muted ? "text-red-300 bg-red-500/10 border-red-500/30" : "text-slate-200 bg-white/5"} hover:bg-white/10`}>
              {slip.notifications_muted ? <><BellOff className="h-4 w-4 mr-2" /> Muted</> : <><Bell className="h-4 w-4 mr-2" /> Notify</>}
            </Button>
            <Button data-testid="detail-refresh" variant="outline" onClick={refresh} disabled={refreshing} className="border-white/10 text-slate-200 bg-white/5 hover:bg-white/10">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh Live
            </Button>
            <Button data-testid="detail-delete" variant="outline" onClick={del} className="border-red-500/30 text-red-300 bg-red-500/10 hover:bg-red-500/20">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </div>
        </div>

        {(slip.screenshot_paths?.length > 0 || slip.screenshot_path) && (
          <div className="mb-6">
            <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3 flex items-center gap-2">
              <ImageIcon className="h-3 w-3" /> Screenshots
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {(slip.screenshot_paths?.length ? slip.screenshot_paths : [slip.screenshot_path]).filter(Boolean).map((p) => {
                const token = localStorage.getItem("nfl_bet_token") || "";
                const src = `${BACKEND_URL}/api/files/${p}?auth=${encodeURIComponent(token)}`;
                return (
                  <button
                    key={p}
                    data-testid={`slip-screenshot-${p}`}
                    onClick={() => setLightbox(src)}
                    className="aspect-[3/4] rounded-lg overflow-hidden border border-white/10 bg-black/40 hover:border-emerald-500/40 transition-colors"
                  >
                    <img src={src} alt="bet slip screenshot" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Legs</h2>
        <div className="space-y-3">
          {slip.legs.map((leg, i) => (
            <div key={leg.leg_id || i} className="rounded-xl bg-[#121824] border border-white/10 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {leg.away_logo && <img src={leg.away_logo} alt="" className="h-8 w-8" />}
                  <div>
                    <div className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                      {leg.away_team} @ {leg.home_team}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{leg.game_detail || "Scheduled"}</div>
                  </div>
                  {leg.home_logo && <img src={leg.home_logo} alt="" className="h-8 w-8" />}
                </div>
                <StatusBadge status={leg.status} testid={`detail-leg-status-${i}`} />
              </div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-white font-semibold">{leg.pick}</div>
                <div className="font-mono text-emerald-300 font-bold">{formatOdds(leg.odds, settings.odds_format)}</div>
              </div>
              {typeof leg.home_score === "number" && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <ScoreBox team={leg.away_team} score={leg.away_score} />
                  <ScoreBox team={leg.home_team} score={leg.home_score} />
                </div>
              )}
              {leg.market_type === "player_prop" && leg.line ? (
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-400">{leg.player_name} · {leg.stat_type}</span>
                    <span className="font-mono text-slate-300">{leg.live_stat_value ?? 0} / {leg.line}</span>
                  </div>
                  <Progress value={leg.line ? Math.min(100, (leg.live_stat_value / leg.line) * 100) : 0} className="h-2 bg-white/10" />
                </div>
              ) : (
                leg.live_note && <div className="text-xs text-slate-400 font-mono">{leg.live_note}</div>
              )}
            </div>
          ))}
        </div>
      </main>
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl bg-[#0A0D14] border-white/10 p-2">
          {lightbox && <img src={lightbox} alt="" className="w-full h-auto rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono font-bold text-lg sm:text-xl ${accent === "emerald" ? "text-emerald-300" : "text-white"}`}>{value}</div>
    </div>
  );
}
function ScoreBox({ team, score }) {
  return (
    <div className="rounded-md bg-[#0E1420] border border-white/5 px-3 py-2 flex items-center justify-between">
      <span className="text-slate-400 text-xs uppercase tracking-widest">{team}</span>
      <span className="font-mono font-bold text-xl text-white">{score}</span>
    </div>
  );
}
